import { Route } from '@angular/router';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { authMatcher } from '@processpuzzle/auth';
import { ORG_ADMIN_ROUTES } from '@processpuzzle/org-admin';
import { CUSTOMER_PORTAL_ROUTES, SUBSCRIPTION_ROUTES } from '@processpuzzle/platform-subscription';

/**
 * Path segments this application uses for itself, which therefore cannot be read as a tenant key.
 *
 * **This list is load-bearing, not tidiness.** `currentOrgKey()` returns the first URL path segment
 * whenever it *looks* like an organization key, and `looksLikeOrgKey('signup')` is true — so
 * `main.ts` would feed `signup` into `resolveTenantRealm` and substitute the Keycloak realm to a
 * realm named `signup`, which does not exist. The funnel's submit could then never authenticate.
 * `/welcome` and `/services` have the same latent bug and are fixed by the same list.
 *
 * Core documents that it deliberately does not filter reserved names: `ReservedOrganizationKeys`
 * lives on the backend and the resolver runs before the first HTTP call, so it cannot consult it.
 * The filtering therefore belongs here, to the application that owns these segments.
 *
 * `signup` and `portal` are both already in the backend's `ReservedOrganizationKeys.DEFAULTS`, so no
 * customer can ever claim either as an organization key — the two sets agree by construction rather
 * than by being kept in step. `welcome` is **not** reserved server-side, which means a customer
 * could claim it and have their tenant URL shadowed by this application's own route. That
 * is pre-existing and would be closed by a one-line addition to that backend list.
 */
export const RESERVED_PATH_SEGMENTS = ['welcome', 'services', 'business-starters', 'ai-consultant', 'contact', 'signup', 'portal', 'auth'];

/**
 * The application's routes, as a function of the tenant the URL named.
 *
 * A function rather than a constant because the org-admin branch is mounted under a **literal**
 * `<orgKey>/admin` path built at bootstrap, not under a `:orgKey` parameter. Two reasons:
 *
 * - `ORG_ADMIN_ORG_KEY` is injected when `OrganizationUserService` is constructed, and that service
 *   is `providedIn: 'root'` — so the key must be a value settled before the first route activates.
 *   The same is true of the Keycloak realm, substituted in `main.ts`.
 * - Because the path is literal, a URL naming a *different* tenant matches nothing and the browser
 *   reloads. That is the behaviour we want: switching tenants must re-resolve the realm, or org B's
 *   data would be fetched with a token minted for org A.
 *
 * Nesting `ORG_ADMIN_ROUTES` under a two-segment prefix is safe even though
 * `BaseFormNavigatorSingletonStore` rebuilds details URLs from the entity name: it prefers the
 * breadcrumb, whose `baseUrl` accumulates from the root down to the route that declares `entityName`
 * in its own `routeConfig.data` — which `ORG_ADMIN_ROUTES` does, on the segment-contributing route.
 * `apps/processpuzzle-admin-frontend` had to spread its library routes at the top level for exactly the reason
 * that does not apply here.
 *
 * The header and the sidenav both render `router.config.filter(route => route.title)`, which is why
 * every navigable route carries a `title` and the auth matcher and the redirects deliberately do not.
 */
export function createAppRoutes(orgKey?: string): Route[] {
  const orgAdminBranch: Route[] = orgKey
    ? [
        {
          path: `${orgKey}/admin`,
          title: 'ProcessPuzzle - Administration',
          data: { icon: 'group', menuTitle: 'admin.users' },
          children: ORG_ADMIN_ROUTES,
        },
        // `/<orgKey>` on its own is the tenant's front door; send it to the only surface that exists
        // for a tenant today.
        { path: orgKey, pathMatch: 'full', redirectTo: `${orgKey}/admin` },
      ]
    : [];

  return [
    {
      // The public landing page: no auth resolver, deliberately. It is the one surface a visitor who
      // has never heard of ProcessPuzzle is supposed to reach, and bouncing them to Keycloak first
      // would defeat the point of having it.
      //
      path: 'welcome',
      title: 'ProcessPuzzle - Welcome',
      data: { icon: 'auto_awesome', menuTitle: 'welcome' },
      loadComponent: () => import('./content/content.component').then((component) => component.ContentComponent),
    },
    {
      path: 'services',
      title: 'ProcessPuzzle - Services',
      data: { icon: 'work', menuTitle: 'services' },
      loadComponent: () => import('./services/services.component').then((component) => component.ServicesComponent),
    },
    {
      // Public, like the landing page: the catalog is what a visitor browses before deciding to sign up.
      path: 'business-starters',
      title: 'ProcessPuzzle - Business Starters',
      data: { icon: 'storefront', menuTitle: 'businessStarters' },
      loadComponent: () => import('./business-starters/business-starters.component').then((component) => component.BusinessStartersComponent),
    },
    {
      // Public as well: it explains the feature to visitors and collects early-access requests.
      path: 'ai-consultant',
      title: 'ProcessPuzzle - AI Consultant',
      data: { icon: 'smart_toy', menuTitle: 'aiConsultant' },
      loadComponent: () => import('./ai-consultant/ai-consultant.component').then((component) => component.AiConsultantComponent),
    },
    {
      path: 'contact',
      title: 'ProcessPuzzle - Contact',
      data: { icon: 'mail', menuTitle: 'contact' },
      loadComponent: () => import('./contact/contact.component').then((component) => component.ContactComponent),
    },
    {
      // Public, deliberately: a visitor comparing prices must reach the plan list without being
      // bounced to Keycloak. The contract requires an authenticated, email-verified account only to
      // *submit*, and `SubscriptionFunnelComponent.submit` is where `authenticate()` runs.
      //
      // Before the org-admin branch, and it matters: `signup` is in RESERVED_PATH_SEGMENTS, so
      // `main.ts` never reads it as a tenant and the branch below is built for whatever tenant the
      // URL did name — but a literal `signup` route placed after a `signup/admin` branch would be
      // unreachable if a tenant were ever called that. It cannot be, because the segment is
      // reserved on the backend too; the ordering is belt and braces on a rule enforced elsewhere.
      path: 'signup',
      title: 'ProcessPuzzle - Subscription',
      data: { icon: 'shopping_cart', menuTitle: 'subscription' },
      children: SUBSCRIPTION_ROUTES,
    },
    {
      // The customer portal. No `:orgKey` in the path: there is no principal on the gateway hop to
      // check one against, so a URL carrying the key would be a shareable link that reads somebody's
      // invoices. The key comes from what a completed sign-up remembered on this browser, or from
      // the portal's own prompt.
      //
      // The whole surface answers 404 unless `platform-subscription.portal.enabled` is true on the
      // backend, which it is not by default — see that flag's comment in the backend's
      // application.yaml for what has to exist before it may be.
      path: 'portal',
      title: 'ProcessPuzzle - Customer Home',
      data: { icon: 'account_balance_wallet', menuTitle: 'customerHome' },
      children: CUSTOMER_PORTAL_ROUTES,
    },
    ...orgAdminBranch,
    // Only reachable for `/`, which by definition names no tenant.
    { path: '', pathMatch: 'full', redirectTo: 'welcome' },
    {
      // Matches any URL containing `auth`, which is how the library's callback and profile routes are
      // reached regardless of the provider's redirect shape.
      matcher: authMatcher,
      loadChildren: () => import('@processpuzzle/auth/feature').then((routes) => routes.authRoutes),
    },
  ];
}
